import { DeductionListJson } from "@/api"
import { ColumnFiltersState, ColumnSort, PaginationState, VisibilityState } from "@tanstack/react-table"
import { subMonths } from "date-fns"
import { entity, persistence } from "simpler-state"
import { IGlobalFilterState } from "@/global_filter"

interface ITableState {
  search: string
  rowSelection: DeductionListJson | {}
  sorting: ColumnSort[]
  columnVisibility: VisibilityState
  pagination: PaginationState
  columnFilters: ColumnFiltersState
  earlyPay: boolean
  /** Only deductions with a pending task assigned to the current user (filtered server-side). */
  assignedToMe: boolean
}

export const INITIAL_TABLE_STATE: ITableState = {
  search: "",
  earlyPay: false,
  assignedToMe: false,
  rowSelection: {},
  columnFilters: [],
  columnVisibility: {
    backup_parse: false,
    check_amount: false,
    check_number: false,
    execution_date: false,
    dc_name: false,
    reason_code_type: false,
    product_line: false,
    retailer_invoice_number: false,
    validated: false,
    custom_deduction_method: false,
    executive_mapping: false,
    dispute_likelihood: false,
    accounting_status: false,
    custom_category: false,
    has_splits: false,
    is_parent: false,
    customer_number: false,
    source: false,
    import_source: false,
    pending: false,
    coded: false,
    // `remote_accounting_id` is deliberately absent: it is an org field now, so an org that turns
    // Accounting ID on in Settings should see the column rather than have to un-hide it. The key
    // used to sit here suppressing nothing — the deduction table had no such column.
    admin_fee: false,
    days_in_stage: false,
    dispute_expires: false,
  },
  pagination: { pageIndex: 0, pageSize: 50 }, // todo(joey): maybe just don't save this in the sticky state?
  sorting: [{ id: "validated", desc: false }],
}

export const INITIAL_GLOBAL_FILTER_STATE: IGlobalFilterState = {
  startDate: subMonths(new Date(), 6),
  endDate: new Date(),
  distributor: null,
  codedFilter: "",
  dateKind: "check",
}

function deserializeFn(blob: string): ITableState {
  let data = JSON.parse(blob)
  data.search = data.search ?? INITIAL_TABLE_STATE.search
  data.rowSelection = data.rowSelection ?? INITIAL_TABLE_STATE.rowSelection
  data.sorting = data.sorting ?? INITIAL_TABLE_STATE.sorting
  data.columnVisibility = {
    ...INITIAL_TABLE_STATE.columnVisibility,
    ...(data.columnVisibility ?? {}),
  }
  data.pagination = data.pagination ?? INITIAL_TABLE_STATE.pagination
  data.columnFilters = data.columnFilters ?? INITIAL_TABLE_STATE.columnFilters
  data.earlyPay = data.earlyPay ?? INITIAL_TABLE_STATE.earlyPay
  data.assignedToMe = data.assignedToMe ?? INITIAL_TABLE_STATE.assignedToMe
  return data
}

export const TableState = entity(INITIAL_TABLE_STATE, [persistence("deduction_table_state", { deserializeFn })])
